import { useState, useEffect } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PlanRestructureInbox — surface Claude-generated scope-lock suggestions
 * awaiting Shannon's approval. Renders inline on the Field Mode tab AND
 * inside the Projects tab so it's discoverable from both contexts.
 *
 * Each suggestion shows: project, summary, added/removed scope, budget delta,
 * timeline delta, risk flags, recommended action. Shannon hits Approve/Reject/Escalate.
 */
const PlanRestructureInbox = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deciding, setDeciding] = useState(null);
  const [toast, setToast] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/plan-restructure?status=pending&limit=10`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        setSuggestions(d.suggestions || []);
      }
    } catch (_) { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const decide = async (sid, decision) => {
    if (decision === "rejected" && !window.confirm("Reject this suggestion? It won't appear again.")) return;
    setDeciding(sid);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/plan-restructure/${sid}/decide`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ decision, notes: "" }),
      });
      if (r.ok) {
        setToast({ ok: true, msg: `Marked ${decision}.` });
        load();
      } else {
        const d = await r.json().catch(() => ({}));
        setToast({ ok: false, msg: d.detail || "Failed." });
      }
    } catch (_) {
      setToast({ ok: false, msg: "Network error." });
    }
    setDeciding(null);
    setTimeout(() => setToast(null), 5000);
  };

  if (loading && suggestions.length === 0) return null;
  if (!loading && suggestions.length === 0) return null;

  return (
    <div data-testid="plan-restructure-inbox" style={{
      background: "linear-gradient(135deg, rgba(184,145,90,0.10) 0%, rgba(122,143,126,0.06) 100%)",
      border: "1px solid rgba(184,145,90,0.4)", borderLeft: "4px solid #B8915A",
      borderRadius: 8, padding: "1rem 1.2rem", marginBottom: "1rem"
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "#B8915A", fontWeight: 700 }}>
            🧠 AI Scope-Lock Suggestions
          </div>
          <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: 14, marginTop: 2 }}>
            {suggestions.length} restructure{suggestions.length === 1 ? "" : "s"} pending your approval
          </div>
        </div>
        <button type="button" onClick={load} disabled={loading}
                data-testid="restructure-refresh"
                style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #4B5563",
                         padding: "0.3rem 0.7rem", borderRadius: 4, fontSize: 11, cursor: "pointer", fontWeight: 600 }}>
          {loading ? "…" : "Refresh"}
        </button>
      </div>

      {toast && (
        <div style={{
          background: toast.ok ? "rgba(34,197,94,0.12)" : "rgba(220,38,38,0.12)",
          border: `1px solid ${toast.ok ? "rgba(34,197,94,0.4)" : "rgba(220,38,38,0.4)"}`,
          color: toast.ok ? "#86EFAC" : "#FCA5A5",
          padding: "0.5rem 0.8rem", borderRadius: 5, fontSize: 13, marginBottom: 8
        }}>
          {toast.ok ? "✓ " : "⚠ "}{toast.msg}
        </div>
      )}

      <div style={{ display: "grid", gap: 8 }}>
        {suggestions.map(s => {
          const p = s.parsed || {};
          const isOpen = !!expanded[s.suggestion_id];
          const flagColor = (p.recommended_action === "escalate_to_owner") ? "#FCA5A5"
                          : (p.recommended_action === "field_issue_owner_trigger") ? "#FFB347"
                          : "#86EFAC";
          return (
            <div key={s.suggestion_id} data-testid={`restructure-card-${s.suggestion_id}`} style={{
              background: "rgba(15,23,42,0.55)", border: "1px solid rgba(255,255,255,0.06)",
              borderLeft: `3px solid ${flagColor}`, borderRadius: 6, padding: "0.7rem 0.9rem"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 280px" }}>
                  <div style={{ color: "#9CA3AF", fontSize: 11, marginBottom: 2 }}>
                    {s.project_address || s.project_id || "Project"}
                  </div>
                  <div style={{ color: "#F3F4F6", fontWeight: 700, fontSize: 14, lineHeight: 1.45 }}>
                    {p.summary || "(no summary)"}
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap", fontSize: 12, color: "#D1D5DB" }}>
                    {typeof p.budget_delta === "number" && (
                      <span style={{ color: p.budget_delta > 0 ? "#FFB347" : "#86EFAC" }}>
                        💰 {p.budget_delta > 0 ? "+" : ""}${Math.round(p.budget_delta)}
                      </span>
                    )}
                    {typeof p.timeline_delta_days === "number" && (
                      <span style={{ color: p.timeline_delta_days > 0 ? "#FFB347" : "#86EFAC" }}>
                        ⏱ {p.timeline_delta_days > 0 ? "+" : ""}{p.timeline_delta_days}d
                      </span>
                    )}
                    {p.recommended_action && (
                      <span style={{ color: flagColor }}>→ {p.recommended_action.replace(/_/g, " ")}</span>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "flex-start" }}>
                  <button type="button" onClick={() => setExpanded(prev => ({ ...prev, [s.suggestion_id]: !prev[s.suggestion_id] }))}
                          data-testid={`restructure-expand-${s.suggestion_id}`}
                          style={{ background: "transparent", color: "#9CA3AF", border: "1px solid #4B5563",
                                   padding: "0.35rem 0.7rem", borderRadius: 4, fontSize: 11, cursor: "pointer", fontWeight: 600 }}>
                    {isOpen ? "Hide" : "Detail"}
                  </button>
                </div>
              </div>

              {isOpen && (
                <div data-testid={`restructure-detail-${s.suggestion_id}`}
                     style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid rgba(255,255,255,0.06)", color: "#D1D5DB", fontSize: 12.5, lineHeight: 1.55 }}>
                  {p.added_scope && p.added_scope.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ color: "#86EFAC", fontWeight: 700, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 3 }}>+ Add</div>
                      <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                        {p.added_scope.map((it, i) => (
                          <li key={i} style={{ marginBottom: 3 }}>
                            <strong>{it.item}</strong> — {it.reason}
                            {(it.est_cost_low || it.est_cost_high) && (
                              <span style={{ color: "#9CA3AF" }}> (${it.est_cost_low || "?"}–${it.est_cost_high || "?"})</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {p.removed_scope && p.removed_scope.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ color: "#FCA5A5", fontWeight: 700, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 3 }}>- Drop</div>
                      <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                        {p.removed_scope.map((it, i) => (
                          <li key={i} style={{ marginBottom: 3 }}>
                            <strong>{it.item}</strong> — {it.reason}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {p.risk_flags && p.risk_flags.length > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ color: "#FFB347", fontWeight: 700, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 3 }}>⚠ Risk Flags</div>
                      <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                        {p.risk_flags.map((rf, i) => <li key={i}>{rf}</li>)}
                      </ul>
                    </div>
                  )}
                  {p.notes_to_shannon && (
                    <div style={{ marginTop: 6, padding: "6px 10px", background: "rgba(184,145,90,0.08)",
                                  border: "1px solid rgba(184,145,90,0.25)", borderRadius: 4,
                                  color: "#D1D5DB", fontStyle: "italic" }}>
                      💬 {p.notes_to_shannon}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: "flex", gap: 6, marginTop: 9, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button type="button" onClick={() => decide(s.suggestion_id, "rejected")}
                        disabled={deciding === s.suggestion_id}
                        data-testid={`restructure-reject-${s.suggestion_id}`}
                        style={{ background: "transparent", color: "#FCA5A5", border: "1px solid #FCA5A5",
                                 padding: "0.35rem 0.85rem", borderRadius: 4, fontWeight: 700, fontSize: 11, cursor: "pointer" }}>
                  ✕ Reject
                </button>
                <button type="button" onClick={() => decide(s.suggestion_id, "escalated")}
                        disabled={deciding === s.suggestion_id}
                        data-testid={`restructure-escalate-${s.suggestion_id}`}
                        style={{ background: "transparent", color: "#FFB347", border: "1px solid #FFB347",
                                 padding: "0.35rem 0.85rem", borderRadius: 4, fontWeight: 700, fontSize: 11, cursor: "pointer" }}>
                  ⤴ Escalate
                </button>
                <button type="button" onClick={() => decide(s.suggestion_id, "approved")}
                        disabled={deciding === s.suggestion_id}
                        data-testid={`restructure-approve-${s.suggestion_id}`}
                        style={{ background: "#86EFAC", color: "#0F172A", border: "1px solid #5e7263",
                                 padding: "0.35rem 1rem", borderRadius: 4, fontWeight: 800, fontSize: 11, cursor: "pointer" }}>
                  ✓ Approve into Scope-Lock
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlanRestructureInbox;
